"use client"

import React, { useState, useEffect } from 'react'
import { Star } from "lucide-react"
import { useTranslation } from 'react-i18next'
import { watchlistApi, userEntityLinksApi } from "@/api/api"
import { invalidateWatchlistSummaryCache } from '@/hooks/useWatchlistSummary'
import { emitViewCountsInvalidation } from '@/features/list-page/hooks/viewCountsInvalidationBus'
import { toast } from "sonner"
import type { EntityType } from '@/types/watchlist'

interface WatchButtonProps {
  entityType: EntityType | string
  entityId: string
  className?: string
  showLabel?: boolean
  otherWatchersCount?: number
}

/**
 * WatchButton —— 单击 toggle 加/取关注
 *
 * 设计要点(redesign-watchlist-via-views OpenSpec change + 后续 follow-up):
 *
 *   1. **单点查询**:挂载时只查 "当前用户 × 这一个 entity 是否有 watcher link",
 *      一行结果即可,不再拉整个 entityType 的 watchlist 然后 client-filter。
 *      用 CURRENT_USER_ID sentinel 让后端 FilterProcessor 替换为当前请求人。
 *
 *   2. **乐观更新**:点击 ☆ 立刻翻 UI 状态(灰 → 黄 / 黄 → 灰),不等 API 完成。
 *      失败时回滚 + toast 错误。这样用户体验"瞬间响应",不会出现"点了没反应,
 *      再点又说已经 watched"的尴尬。
 *
 *   3. **dropdown summary 失效**:写操作完成后调 invalidateWatchlistSummaryCache(),
 *      让右上角 dropdown 的扁平计数立刻刷新。
 *
 *   4. **关注是布尔的**:不再弹 GroupSelectDialog,linkDescription 始终为 null。
 */
export function WatchButton({
  entityType,
  entityId,
  className = "",
  showLabel = false,
  otherWatchersCount = 0,
}: WatchButtonProps) {
  const { t } = useTranslation('common')
  const [isWatching, setIsWatching] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // 单点查询:当前用户是否关注了这个具体 entity
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const response: any = await userEntityLinksApi.search({
          filters: {
            userId:     { type: 'string', operator: 'equals', value: 'CURRENT_USER_ID' },
            entityType: { type: 'string', operator: 'equals', value: entityType },
            entityId:   { type: 'string', operator: 'equals', value: entityId },
            linkType:   { type: 'string', operator: 'equals', value: 'watcher' },
          },
          pageSize: 1,
          columns: 'id',
        })
        if (cancelled) return
        const items: any[] = response?.results ?? response?.data?.results ?? []
        setIsWatching(items.length > 0)
      } catch (e) {
        // 静默失败 — 默认按 not-watching 渲染,用户点了再说
        console.error('[WatchButton] watched-check error:', e)
      }
    })()
    return () => { cancelled = true }
  }, [entityType, entityId])

  const toggleWatch = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (isLoading) return

    // 乐观更新 —— 立刻翻状态,失败时回滚
    const prevState = isWatching
    setIsWatching(!prevState)
    setIsLoading(true)

    try {
      if (prevState) {
        await watchlistApi.unwatchEntity(entityType, entityId)
        toast.success(t('ui.watchButton.unwatched'))
      } else {
        await watchlistApi.watchEntity(entityType, entityId)
        toast.success(t('ui.watchButton.watched'))
      }
      // 让 dropdown 数字刷新
      invalidateWatchlistSummaryCache()
      // 让 ViewPanel 的 My Watchlist 计数 badge 强制全量重拉。
      // watch/unwatch 写 user_entity_links(per-user 链接表),后端**没有**版本
      // 信号(stateToken 条件读会回 notModified),行为人本端的即时性全靠这一钩子
      // (机制③,openspec add-view-counts-version-invalidation)。
      emitViewCountsInvalidation()
    } catch (error: any) {
      console.error('[WatchButton] toggle error:', error)

      // 失败处理 —— 区分两种情况:
      //   a) 服务端说 "already exists"(用户已 watch 但本地状态不对)→ 强制为 true
      //   b) 真正失败 → 回滚到上一个状态
      const alreadyExists =
        error?.response?.data?.message?.includes('already exists') ||
        error?.message?.includes('already exists')

      if (alreadyExists) {
        setIsWatching(true)
        invalidateWatchlistSummaryCache()  // 同步 dropdown 状态
        emitViewCountsInvalidation()       // badge 同步(本地状态与服务端不一致时对齐)
        toast.error(t('ui.watchButton.alreadyWatching'))
      } else {
        setIsWatching(prevState)  // 回滚
        toast.error(prevState ? t('ui.watchButton.failedUnwatch') : t('ui.watchButton.failedWatch'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const totalWatchers = otherWatchersCount + (isWatching ? 1 : 0)

  return (
    <button
      onClick={(e) => { void toggleWatch(e) }}
      disabled={isLoading}
      className={`
        relative flex items-center gap-1.5 transition-all font-medium text-xs border border-transparent
        ${isWatching
          ? 'text-amber-400 hover:text-amber-500'
          : 'text-slate-300 hover:text-muted-foreground'
        }
        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      title={isWatching ? t('ui.watchButton.unwatch') : t('ui.watchButton.watch')}
      aria-label={isWatching ? t('ui.watchButton.unwatchAria') : t('ui.watchButton.watchAria')}
    >
      {isWatching ? (
        <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
      ) : (
        <Star className="w-5 h-5 text-slate-300" />
      )}

      {showLabel && isWatching && <span>{t('ui.watchButton.watching')}</span>}

      {/* Watcher Count Badge (Show if multiple users are watching) */}
      {otherWatchersCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full ring-2 ring-white px-0.5">
          {totalWatchers}
        </span>
      )}
    </button>
  )
}
